// The bar at the top of the Dashboard and Trades pages: how amounts show, filters (instrument,
// side, result, tags, strategy, day and time), a date range, and the accounts to include.
// Filters and the range apply to everything on those two pages.
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import DisplaySwitch from './DisplaySwitch';
import { AccountSelect } from './AppNav';
import { rootSymbol } from './tools/contracts';
import { dayKey, netPnl, tradingDayKey, currentTradingDay } from './pnl';
import { tagKind } from './tags';
import { currentLocale } from './langState';
import { useI18n } from './i18n';

export const EMPTY_FILTERS = {
  symbols: [], sides: [], results: [], reviewed: [], grades: [], tags: [], plays: [], weekdays: [], timeFrom: '', timeTo: '',
};
export const ALL_TIME = { preset: 'all', from: '', to: '' };
const RANGE_KEY = 'mm-date-range';

const RANGE_PRESETS = [
  ['all', 'All time'],
  ['today', 'Today'],
  ['week', 'This week'],
  ['month', 'This month'],
  ['30d', 'Last 30 days'],
  ['90d', 'Last 90 days'],
  ['lastmonth', 'Last month'],
  ['quarter', 'This quarter'],
  ['ytd', 'Year to date'],
];

const keyDate = (key) => new Date(`${key}T12:00:00`);
const shift = (key, days) => { const d = keyDate(key); d.setDate(d.getDate() + days); return dayKey(d); };

export function readRange() {
  try {
    const saved = JSON.parse(localStorage.getItem(RANGE_KEY));
    if (saved && RANGE_PRESETS.some(([k]) => k === saved.preset)) return { ...ALL_TIME, preset: saved.preset };
    if (saved && saved.preset === 'custom' && saved.from && saved.to) return saved;
  } catch { /* nothing saved */ }
  return ALL_TIME;
}

// Whether the trader has ever picked a date range on this browser.
export function rangeSaved() {
  try { return Boolean(localStorage.getItem(RANGE_KEY)); } catch { return false; }
}

export function saveRange(range) {
  try { localStorage.setItem(RANGE_KEY, JSON.stringify(range)); } catch { /* not remembered */ }
}

// The first and last trading day (YYYY-MM-DD) a range covers, or null for all time.
export function rangeBounds(range) {
  const today = currentTradingDay();
  const d = keyDate(today);
  const ymd = (y, m, day) => dayKey(new Date(y, m, day, 12));
  switch (range.preset) {
    case 'today': return { from: today, to: today };
    case 'week': return { from: shift(today, -d.getDay()), to: today };
    case 'month': return { from: ymd(d.getFullYear(), d.getMonth(), 1), to: today };
    case '30d': return { from: shift(today, -29), to: today };
    case '90d': return { from: shift(today, -89), to: today };
    case 'lastmonth': return { from: ymd(d.getFullYear(), d.getMonth() - 1, 1), to: ymd(d.getFullYear(), d.getMonth(), 0) };
    case 'quarter': return { from: ymd(d.getFullYear(), Math.floor(d.getMonth() / 3) * 3, 1), to: today };
    case 'ytd': return { from: ymd(d.getFullYear(), 0, 1), to: today };
    case 'custom': return range.from && range.to ? { from: range.from, to: range.to } : null;
    default: return null;
  }
}

const minutesOf = (date) => { const d = new Date(date); return d.getHours() * 60 + d.getMinutes(); };
const toMinutes = (hhmm) => { const [h, m] = hhmm.split(':').map(Number); return h * 60 + m; };

// How many kinds of filter are switched on.
export function countFilters(f) {
  return ['symbols', 'sides', 'results', 'reviewed', 'grades', 'tags', 'plays', 'weekdays'].filter((k) => f[k].length).length
    + (f.timeFrom || f.timeTo ? 1 : 0);
}

// The trades that pass the filters and fall inside the date range.
export function applyFilters(trades, f, range) {
  const bounds = rangeBounds(range);
  const lower = (list) => list.map((x) => x.toLowerCase());
  const tags = lower(f.tags);
  const plays = lower(f.plays);
  return trades.filter((tr) => {
    if (bounds) {
      const key = tradingDayKey(tr.date);
      if (key < bounds.from || key > bounds.to) return false;
    }
    if (f.symbols.length && !f.symbols.includes(rootSymbol(tr.symbol))) return false;
    if (f.sides.length && !f.sides.includes(tr.side || 'Long')) return false;
    if (f.results.length) {
      const net = netPnl(tr);
      if (!f.results.includes(net > 0 ? 'win' : net < 0 ? 'loss' : 'even')) return false;
    }
    if (f.reviewed.length) {
      const reviewed = Boolean((tr.tags && tr.tags.length) || (tr.notes && tr.notes.trim()));
      if (!f.reviewed.includes(reviewed ? 'yes' : 'no')) return false;
    }
    if (f.grades.length && !f.grades.includes(tr.grade || 'none')) return false;
    const own = lower(tr.tags || []);
    if (tags.length && !tags.some((x) => own.includes(x))) return false;
    if (plays.length && !plays.some((x) => own.includes(x))) return false;
    if (f.weekdays.length && !f.weekdays.includes(keyDate(tradingDayKey(tr.date)).getDay())) return false;
    if (f.timeFrom || f.timeTo) {
      if (tr.timeUnknown) return false;
      const m = minutesOf(tr.date);
      if (f.timeFrom && m < toMinutes(f.timeFrom)) return false;
      if (f.timeTo && m >= toMinutes(f.timeTo)) return false;
    }
    return true;
  });
}

// A button that opens a panel under it; a click outside or Escape closes it.
function Pop({ label, active, className = '', children, open, setOpen, onClear, clearLabel }) {
  const box = useRef(null);
  const panel = useRef(null);
  // Opens toward whichever side has room, so the page never needs scrolling sideways.
  const [left, setLeft] = useState(false);
  useLayoutEffect(() => {
    if (!open || !box.current || !panel.current) return;
    const btn = box.current.getBoundingClientRect();
    const width = panel.current.offsetWidth;
    setLeft(btn.left + width > window.innerWidth - 8 && btn.right - width >= 8);
  }, [open]);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => box.current && !box.current.contains(e.target) && setOpen(false);
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open, setOpen]);
  return (
    <div className={`tb-pop ${className}`} ref={box}>
      <button type="button" className={`tb-button${active ? ' active' : ''}${open ? ' open' : ''}${onClear ? ' has-clear' : ''}`} aria-expanded={open} onClick={() => setOpen(!open)}>
        {label}<span className="tb-caret" aria-hidden="true">▾</span>
      </button>
      {onClear && <button type="button" className="tb-clear" aria-label={clearLabel} title={clearLabel} onClick={onClear}>&times;</button>}
      {open && <div ref={panel} className={`tb-panel${left ? ' open-left' : ''}`}>{children}</div>}
    </div>
  );
}

function Checks({ options, value, onChange }) {
  const toggle = (v) => onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  return (
    <div className="tb-checks">
      {options.map(([v, label, extra]) => (
        <label key={v} className="tb-check">
          <input type="checkbox" checked={value.includes(v)} onChange={() => toggle(v)} />
          <span>{label}</span>
          {extra !== undefined && <small>{extra}</small>}
        </label>
      ))}
    </div>
  );
}

const TIMES = Array.from({ length: 48 }, (_, i) => `${String(Math.floor(i / 2)).padStart(2, '0')}:${i % 2 ? '30' : '00'}`);
const timeLabel = (hhmm) => new Date(2026, 0, 1, ...hhmm.split(':').map(Number)).toLocaleTimeString(currentLocale(), { hour: 'numeric', minute: '2-digit' });

function FilterMenu({ trades, library, plays, filters, onApply, onClose }) {
  const { t } = useI18n();
  const [tab, setTab] = useState('general');
  const [draft, setDraft] = useState(filters);
  const set = (k) => (v) => setDraft((d) => ({ ...d, [k]: v }));
  const count = (pred) => trades.filter(pred).length;

  const symbols = [...new Set(trades.map((x) => rootSymbol(x.symbol)))].sort();
  const tagNames = [...new Set(trades.flatMap((x) => x.tags || []))].sort((a, b) => a.localeCompare(b));
  const grades = ['A+', 'A', 'A-', 'B+', 'B', 'B-'];
  const weekdayName = (i) => new Date(2026, 0, 4 + i).toLocaleDateString(currentLocale(), { weekday: 'long' });
  const tabs = [['general', t('General')], ['tags', t('Tags')], ['strategy', t('Strategy')], ['time', t('Day & time')]];
  const groupCount = {
    general: ['symbols', 'sides', 'results', 'reviewed', 'grades'].filter((k) => draft[k].length).length,
    tags: draft.tags.length ? 1 : 0,
    strategy: draft.plays.length ? 1 : 0,
    time: (draft.weekdays.length ? 1 : 0) + (draft.timeFrom || draft.timeTo ? 1 : 0),
  };

  return (
    <div className="tb-filters">
      <div className="tb-filters-body">
        <div className="tb-tabs" role="tablist">
          {tabs.map(([id, label]) => (
            <button key={id} type="button" role="tab" aria-selected={tab === id} className={`tb-tab${tab === id ? ' active' : ''}`} onClick={() => setTab(id)}>
              {label}{groupCount[id] > 0 && <span className="tb-count">{groupCount[id]}</span>}
            </button>
          ))}
        </div>
        <div className="tb-tab-panel">
          {tab === 'general' && (
            <>
              <h4>{t('Instrument')}</h4>
              <Checks value={draft.symbols} onChange={set('symbols')}
                options={symbols.map((s) => [s, s, count((x) => rootSymbol(x.symbol) === s)])} />
              <h4>{t('Side')}</h4>
              <Checks value={draft.sides} onChange={set('sides')}
                options={[['Long', t('Long'), count((x) => (x.side || 'Long') === 'Long')], ['Short', t('Short'), count((x) => x.side === 'Short')]]} />
              <h4>{t('Result')}</h4>
              <Checks value={draft.results} onChange={set('results')}
                options={[['win', t('Winners'), count((x) => netPnl(x) > 0)], ['loss', t('Losers'), count((x) => netPnl(x) < 0)], ['even', t('Break-even'), count((x) => netPnl(x) === 0)]]} />
              <h4>{t('Reviewed')}</h4>
              <Checks value={draft.reviewed} onChange={set('reviewed')}
                options={[['yes', t('Has tags or notes')], ['no', t('Not reviewed yet')]]} />
              <h4>{t('Execution grade')}</h4>
              <Checks value={draft.grades} onChange={set('grades')}
                options={[...grades.map((g) => [g, g, count((x) => x.grade === g)]), ['none', t('No grade'), count((x) => !x.grade)]]} />
            </>
          )}
          {tab === 'tags' && (
            <>
              <p className="tb-hint">{t('Shows trades with any of the tags you pick.')}</p>
              {['setup', 'mistake', 'context'].map((kind) => {
                const names = tagNames.filter((n) => tagKind(n, library) === kind);
                if (!names.length) return null;
                return (
                  <div key={kind}>
                    <h4>{t(kind === 'setup' ? 'Setups' : kind === 'mistake' ? 'Mistakes' : 'Context')}</h4>
                    <Checks value={draft.tags} onChange={set('tags')} options={names.map((n) => [n, n, count((x) => (x.tags || []).includes(n))])} />
                  </div>
                );
              })}
              {!tagNames.length && <p className="empty">{t('No tagged trades yet.')}</p>}
            </>
          )}
          {tab === 'strategy' && (
            <>
              <p className="tb-hint">{t('Your Playbook plays. A trade belongs to a play when it has the play\'s setup tag.')}</p>
              {plays.length ? (
                <Checks value={draft.plays} onChange={set('plays')}
                  options={plays.map((p) => [p.tag, p.name || p.tag, count((x) => (x.tags || []).some((n) => n.toLowerCase() === p.tag.toLowerCase()))])} />
              ) : <p className="empty">{t('No plays in your Playbook yet.')}</p>}
            </>
          )}
          {tab === 'time' && (
            <>
              <h4>{t('Day of the week')}</h4>
              <Checks value={draft.weekdays} onChange={set('weekdays')}
                options={[1, 2, 3, 4, 5, 0, 6].map((i) => [i, weekdayName(i), count((x) => keyDate(tradingDayKey(x.date)).getDay() === i)])} />
              <h4>{t('Time of day')} <small className="tb-sub">{t('(your time, when the trade opened)')}</small></h4>
              <div className="tb-times">
                <label>{t('From')}
                  <select value={draft.timeFrom} onChange={(e) => set('timeFrom')(e.target.value)}>
                    <option value="">{t('Any time')}</option>
                    {TIMES.map((x) => <option key={x} value={x}>{timeLabel(x)}</option>)}
                  </select>
                </label>
                <label>{t('To')}
                  <select value={draft.timeTo} onChange={(e) => set('timeTo')(e.target.value)}>
                    <option value="">{t('Any time')}</option>
                    {TIMES.map((x) => <option key={x} value={x}>{timeLabel(x)}</option>)}
                  </select>
                </label>
              </div>
            </>
          )}
        </div>
      </div>
      <div className="tb-filters-foot">
        <button type="button" className="link-button" onClick={() => setDraft(EMPTY_FILTERS)}>{t('Reset all')}</button>
        <span className="tb-foot-right">
          <button type="button" className="secondary small" onClick={onClose}>{t('Cancel')}</button>
          <button type="button" className="small" onClick={() => onApply(draft)}>{t('Apply filters')}</button>
        </span>
      </div>
    </div>
  );
}

function RangeMenu({ range, onPick }) {
  const { t } = useI18n();
  const bounds = rangeBounds(range);
  const [from, setFrom] = useState(bounds ? bounds.from : '');
  const [to, setTo] = useState(bounds ? bounds.to : '');
  const [month, setMonth] = useState(() => { const d = keyDate(bounds ? bounds.to : currentTradingDay()); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const today = currentTradingDay();
  const first = month.getDay();
  const count = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const cells = [...Array(first).fill(null), ...Array.from({ length: count }, (_, i) => dayKey(new Date(month.getFullYear(), month.getMonth(), i + 1, 12)))];
  const names = Array.from({ length: 7 }, (_, i) => new Date(2026, 0, 4 + i).toLocaleDateString(currentLocale(), { weekday: 'short' }).slice(0, 2));
  const long = (key) => (key ? keyDate(key).toLocaleDateString(currentLocale(), { month: 'short', day: 'numeric', year: 'numeric' }) : '—');

  function click(key) {
    if (!from || to) { setFrom(key); setTo(''); return; }
    if (key < from) { setFrom(key); return; }
    setTo(key);
    onPick({ preset: 'custom', from, to: key });
  }

  return (
    <div className="tb-range">
      <div className="tb-range-cal">
        <div className="tb-range-dates">
          <span className={from && !to ? 'picking' : ''}>{long(from)}</span>
          <span aria-hidden="true">→</span>
          <span className={from && !to ? 'picking' : ''}>{to ? long(to) : (from ? t('Pick the end') : '—')}</span>
        </div>
        <div className="tb-range-head">
          <button type="button" className="secondary small" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))} aria-label={t('Previous month')}>&larr;</button>
          <strong>{month.toLocaleDateString(currentLocale(), { month: 'long', year: 'numeric' })}</strong>
          <button type="button" className="secondary small" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))} aria-label={t('Next month')}>&rarr;</button>
        </div>
        <div className="tb-range-grid">
          {names.map((n, i) => <span key={`${n}${i}`} className="tb-range-wd">{n}</span>)}
          {cells.map((key, i) => {
            if (!key) return <span key={`e${i}`} />;
            const inRange = from && to && key >= from && key <= to;
            const edge = key === from || key === to;
            return (
              <button key={key} type="button" onClick={() => click(key)}
                className={`tb-range-day${inRange ? ' in' : ''}${edge ? ' edge' : ''}${key === today ? ' today' : ''}`}>
                {Number(key.slice(8))}
              </button>
            );
          })}
        </div>
        <p className="tb-hint">{t('Click the first day, then the last day.')}</p>
      </div>
      <div className="tb-presets">
        {RANGE_PRESETS.map(([k, label]) => (
          <button key={k} type="button" className={`tb-preset${range.preset === k ? ' active' : ''}`} onClick={() => onPick({ ...ALL_TIME, preset: k })}>{t(label)}</button>
        ))}
      </div>
    </div>
  );
}

export function rangeLabel(range, t) {
  if (range.preset === 'custom' && range.from && range.to) {
    const f = (key) => keyDate(key).toLocaleDateString(currentLocale(), { month: 'short', day: 'numeric' });
    return `${f(range.from)} – ${f(range.to)}`;
  }
  const preset = RANGE_PRESETS.find(([k]) => k === range.preset);
  return t(preset ? preset[1] : 'All time');
}

export default function TradeToolbar({ trades, library, plays, filters, setFilters, range, setRange, size, accountProps }) {
  const { t } = useI18n();
  const [openMenu, setOpenMenu] = useState(null);
  const menu = (id) => ({ open: openMenu === id, setOpen: (o) => setOpenMenu(o ? id : null) });
  const n = countFilters(filters);
  return (
    <div className="trade-toolbar">
      <DisplaySwitch size={size} />
      <Pop label={n ? t('Filters ({n})', { n }) : t('Filters')} active={n > 0} className="tb-pop-filters" {...menu('filters')}>
        <FilterMenu trades={trades} library={library} plays={plays} filters={filters}
          onApply={(f) => { setFilters(f); setOpenMenu(null); }} onClose={() => setOpenMenu(null)} />
      </Pop>
      <Pop label={range.preset === 'all' ? t('Date range') : rangeText(range, t)} active={range.preset !== 'all'} className="tb-pop-range" {...menu('range')}
        onClear={range.preset !== 'all' ? () => setRange(ALL_TIME) : null} clearLabel={t('Show all dates')}>
        <RangeMenu range={range} onPick={(r) => { setRange(r); setOpenMenu(null); }} />
      </Pop>
      {accountProps && <AccountSelect {...accountProps} className="tb-accounts" />}
    </div>
  );
}

// The date range in full ("Oct 1 – Oct 24, 2026"), or "All time".
export function rangeText(range, t) {
  const b = rangeBounds(range);
  if (!b) return t('All time');
  const f = (key, year) => keyDate(key).toLocaleDateString(currentLocale(), { month: 'short', day: 'numeric', ...(year ? { year: 'numeric' } : {}) });
  return b.from === b.to ? f(b.from, true) : `${f(b.from, b.from.slice(0, 4) !== b.to.slice(0, 4))} – ${f(b.to, true)}`;
}

// The filters in words: ["Shorts", "NQ", "Mondays", "9:30 AM – 10:30 AM"].
export function describeFilters(f, plays, t) {
  const out = [];
  if (f.symbols.length) out.push(f.symbols.join(', '));
  if (f.sides.length === 1) out.push(t(f.sides[0] === 'Short' ? 'Shorts only' : 'Longs only'));
  if (f.results.length && f.results.length < 3) out.push(f.results.map((r) => t(r === 'win' ? 'Winners' : r === 'loss' ? 'Losers' : 'Break-even')).join(', '));
  if (f.reviewed.length === 1) out.push(t(f.reviewed[0] === 'yes' ? 'Has tags or notes' : 'Not reviewed yet'));
  if (f.grades.length) out.push(t('Grade {grades}', { grades: f.grades.map((g) => (g === 'none' ? t('none') : g)).join(', ') }));
  if (f.tags.length) out.push(f.tags.join(', '));
  if (f.plays.length) out.push(f.plays.map((tag) => { const p = plays.find((x) => x.tag === tag); return p ? p.name : tag; }).join(', '));
  if (f.weekdays.length) out.push(f.weekdays.map((i) => new Date(2026, 0, 4 + i).toLocaleDateString(currentLocale(), { weekday: 'short' })).join(', '));
  if (f.timeFrom || f.timeTo) out.push(`${f.timeFrom ? timeLabel(f.timeFrom) : t('Any time')} – ${f.timeTo ? timeLabel(f.timeTo) : t('Any time')}`);
  return out;
}

// Under the bar, while filters or a date range are on: how many trades are shown, what is
// picked, and a way out.
export function FilterNote({ shown, total, filters, range, plays, onClearFilters, onClearRange }) {
  const { t } = useI18n();
  const words = describeFilters(filters, plays, t);
  if (!words.length && range.preset === 'all') return null;
  return (
    <p className="filter-note">
      <strong>{t('Showing {n} of {total} trades', { n: shown, total })}</strong>
      {range.preset !== 'all' && (
        <span className="filter-chip">{rangeText(range, t)}<button type="button" onClick={onClearRange} aria-label={t('Clear the date range')}>×</button></span>
      )}
      {words.map((w) => <span key={w} className="filter-chip plain">{w}</span>)}
      {words.length > 0 && <button type="button" className="link-button" onClick={onClearFilters}>{t('Clear filters')}</button>}
    </p>
  );
}

// The date range button on its own (the Compare page uses it with its own saved range).
export function DateRangeButton({ range, setRange }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  return (
    <div className="trade-toolbar compare-range">
      <Pop label={range.preset === 'all' ? t('Date range') : rangeLabel(range, t)} active={range.preset !== 'all'} className="tb-pop-range" open={open} setOpen={setOpen}>
        <RangeMenu range={range} onPick={(r) => { setRange(r); setOpen(false); }} />
      </Pop>
    </div>
  );
}
